import React, { useState, useEffect, useRef } from 'react';
import BookConsultForm from '../../components/BookConsultForm';
import drVermaImage from '../../assets/images/landing.jpeg'; // adjust path
import { Link } from 'react-router-dom';

const BookConsult = () => {
  const [scrolled, setScrolled] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  
  const heroRef = useRef(null);
  
  // FAQ Data
  const faqItems = [
    {
      question: "What is Nutrigenomics and how does it help?",
      answer: "Nutrigenomics is the study of how food interacts with your specific genes. By checking your genetic markers, we find how your body processes fats, digests sugars, handles stress, absorbs nutrients, and responds to exercise — letting us build a diet plan that works directly with your DNA."
    },
    {
      question: "Do I need to visit the clinic for the DNA test?",
      answer: "No! We send a simple home collection kit to your doorstep. You collect a saliva sample and return it in the prepaid envelope provided. The entire process takes less than 5 minutes and can be done from the comfort of your home."
    },
    {
      question: "Is the ₹999 consultation inclusive of the DNA test?",
      answer: "The ₹999 consultation is an introductory diagnostic session with Dr. Rishabh Verma. Based on your consultation, he will recommend whether the DNA test or gut microbiome test is right for you. These are separate investments priced based on your specific needs."
    },
    {
      question: "How long does it take to see results?",
      answer: "Most clients notice tangible changes in energy and digestion within 2–3 weeks. For weight and hormonal concerns, measurable results typically appear within 6–12 weeks of following the personalized protocol."
    },
    {
      question: "Are the diet plans restrictive or difficult to follow?",
      answer: "Not at all. Our plans are built around your food preferences, cultural background, and lifestyle. The goal is a sustainable way of eating — not a crash diet. We work with what you enjoy, not against it."
    },
    {
      question: "Can I consult from outside India?",
      answer: "Absolutely. Dr. Verma has guided clients across 10+ countries. All consultations are available via video call, and test kits can be shipped internationally. Simply book your slot and our team will coordinate the rest."
    }
  ];
  
  // Scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      if (window.innerWidth <= 768 && heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setStickyVisible(heroBottom <= 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Intersection Observer for scroll reveal
  useEffect(() => {
    const elements = document.querySelectorAll('.step, .testi, .stat-item, .faq-item, .achieve-list li, .who-list li, .sci-layer, .verma-pillar');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    elements.forEach((el, i) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(18px)';
      el.style.transition = `opacity .5s ease ${(i % 4) * 0.07}s, transform .5s ease ${(i % 4) * 0.07}s`;
      observer.observe(el);
    });
    
    return () => observer.disconnect();
  }, []);
  
  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const section = document.querySelector(sectionId);
    if (section) {
      const navH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-h')) || 72;
      window.scrollTo({ top: section.getBoundingClientRect().top + window.scrollY - navH - 16, behavior: 'smooth' });
    }
  };
  
  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? -1 : index);
  };
  
  const handleBookingSuccess = () => {
  };
  
  return (
    <div className="app">
   
      <style>{`
        :root {
          --bg: #faf7f2;
          --bg-cream: #f5ede0;
          --bg-warm: #fefcf7;
          --bg-secondary: #e8f0eb;
          --bg-input: #f2ece3;
          --bg-muted: #f0e9de;
          --forest: #2d5a3d;
          --forest-mid: #4a7c59;
          --sage: #7a9e87;
          --sage-light: #b8d4c0;
          --accent: #b5894a;
          --gold: #c4a882;
          --ink: #1a1208;
          --ink-soft: #3d3020;
          --muted-fg: #5e4f3a;
          --border: #e6ddd0;
          --white: #ffffff;
          --red-bg: #fde8e8;
          --red-border: #f5c0c0;
          --red: #d46060;
          --font-body: 'DM Sans', sans-serif;
          --font-heading: 'Inter', sans-serif;
          --radius-sm: 6px;
          --radius-md: 12px;
          --radius-lg: 20px;
          --radius-xl: 28px;
          --shadow-sm: 0 1px 3px rgba(0,0,0,.08);
          --shadow-md: 0 4px 16px rgba(0,0,0,.08);
          --shadow-lg: 0 16px 48px rgba(0,0,0,.12);
          --nav-h: 72px;
        }

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; font-size: 16px; }
        body { font-family: var(--font-body); background: var(--bg); color: var(--ink); line-height: 1.7; -webkit-font-smoothing: antialiased; margin: 0; }
        img { max-width: 100%; height: auto; display: block; }
        a { color: inherit; text-decoration: none; }
        ul { list-style: none; }
        button { cursor: pointer; border: none; background: none; font: inherit; }
        input, select { font: inherit; }
        iconify-icon { display: inline-block; vertical-align: middle; flex-shrink: 0; }

        .container {
          width: 100%;
          max-width: 1200px;
          margin-inline: auto;
          padding-inline: clamp(1.25rem, 5vw, 2.5rem);
        }
        .hidden { display: none !important; }
        .section { padding-block: clamp(3.5rem, 8vw, 5.5rem); }
        .section--cream  { background: var(--bg-cream); }
        .section--forest { background: var(--forest); }
        .two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(2rem, 5vw, 4rem);
          align-items: start;
        }
        .two-col--vcenter { align-items: center; }
        .section-center { text-align: center; margin-bottom: 3rem; }
        .section-cta { text-align: center; margin-top: 2.5rem; }

        .h1 {
          font-family: var(--font-heading);
          font-size: clamp(2rem, 4.5vw, 3.5rem);
          font-weight: 700;
          color: var(--ink);
          line-height: 1.15;
          margin-bottom: 1.25rem;
        }
        .h1 em { font-style: normal; color: var(--forest); }
        .h2 {
          font-family: var(--font-heading);
          font-size: clamp(1.625rem, 3.5vw, 2.5rem);
          font-weight: 700;
          color: var(--ink);
          line-height: 1.2;
          margin-bottom: 0.875rem;
        }
        .h2 em { font-style: normal; color: var(--forest); }
        .h2--light { color: var(--white); }
        .h2--light em { color: var(--gold); }
        .lead {
          font-size: 1.0625rem;
          color: var(--muted-fg);
          line-height: 1.7;
          margin-bottom: 1.75rem;
          max-width: 520px;
        }
        .lead--center { margin-inline: auto; text-align: center; }
        .lead--light { color: rgba(255,255,255,.75); }
        .body-text { font-size: 0.9375rem; color: var(--muted-fg); line-height: 1.75; }

        .eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.625rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--accent);
          margin-bottom: 1rem;
        }
        .eyebrow--center { display: flex; justify-content: center; }
        .eyebrow--light { color: rgba(255,255,255,.6); }
        .eyebrow__line { display: block; height: 1px; width: 2rem; background: currentColor; opacity: .5; }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-family: var(--font-body);
          font-weight: 600;
          font-size: 0.9375rem;
          border-radius: var(--radius-md);
          padding: 0.75rem 1.5rem;
          transition: opacity .2s, transform .15s, box-shadow .2s;
          cursor: pointer;
          border: none;
        }
        .btn:hover { opacity: .9; transform: translateY(-1px); }
        .btn--primary {
          background: var(--forest);
          color: var(--white);
          box-shadow: 0 4px 14px rgba(45,90,61,.35);
        }
        .btn--gradient {
          background: linear-gradient(135deg, #2d5a3d, #4a7c59);
          color: var(--white);
          box-shadow: 0 6px 20px rgba(45,90,61,.4);
        }
        .btn--gold { background: var(--gold); color: var(--ink); font-weight: 700; }
        .btn--outline { border: 1.5px solid var(--border); color: var(--muted-fg); background: transparent; }
        .btn--full { width: 100%; }
        .btn--lg {
          font-size: 1.0625rem;
          padding: 1rem 2.5rem;
          border-radius: var(--radius-lg);
        }

        .badge {
          display: inline-flex; align-items: center; gap: .5rem;
          background: var(--bg-secondary); border: 1px solid var(--border);
          border-radius: 999px; padding: .375rem 1rem;
          font-size: .75rem; font-weight: 600; color: var(--forest);
          letter-spacing: .04em; text-transform: uppercase; margin-bottom: 1.25rem;
        }
        .badge__dot { width: 6px; height: 6px; border-radius: 50%; background: var(--forest); }

        .pill-row { display: flex; flex-wrap: wrap; gap: .625rem; margin-bottom: 2rem; }
        .pill {
          display: flex; align-items: center; gap: .5rem;
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: 999px; padding: .4rem .875rem;
          font-size: .8125rem; font-weight: 500; color: var(--muted-fg);
        }
        .pill iconify-icon { color: var(--forest); font-size: .8125rem; }

        .chk {
          width: 22px; height: 22px; border-radius: 50%;
          background: var(--bg-secondary); border: 1px solid var(--sage-light);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .chk iconify-icon { color: var(--forest); font-size: .75rem; }
        .xmark {
          width: 20px; height: 20px; border-radius: 50%;
          background: var(--red-bg); border: 1px solid var(--red-border);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .xmark iconify-icon { color: var(--red); font-size: .625rem; }

        .navbar {
          position: sticky; top: 0; z-index: 100;
          height: var(--nav-h);
          background: var(--bg-warm);
          border-bottom: 1px solid var(--border);
          transition: box-shadow .3s;
        }
        .navbar.scrolled { box-shadow: var(--shadow-md); }
        .navbar__inner {
          height: 100%;
          display: flex; align-items: center; justify-content: space-between;
          gap: 1.5rem;
        }
        .brand { display: flex; align-items: center; gap: .5rem; }
        .brand__logo { height: 36px; width: auto; }
        .phone-num {
          display: flex; align-items: center; gap: .375rem;
          font-size: .9rem; font-weight: 500; color: var(--muted-fg);
        }

        .hero {
          background: var(--bg-cream); position: relative; overflow: hidden;
        }
        .hero__blob {
          position: absolute; border-radius: 50%; pointer-events: none;
        }
        .hero__blob--tr {
          width: 420px; height: 420px; top: -20%; right: -6%;
          background: radial-gradient(circle, var(--sage-light), transparent 70%);
          opacity: .28;
        }
        .hero__blob--bl {
          width: 340px; height: 340px; bottom: -15%; left: -8%;
          background: radial-gradient(circle, var(--gold), transparent 70%);
          opacity: .18;
        }
        .hero__inner {
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: clamp(2rem, 4vw, 4rem);
          align-items: start;
          padding-top: 3.5rem;
          padding-bottom: 3.5rem;
          position: relative; z-index: 1;
        }
        .hero__checklist {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: .5rem .75rem; max-width: 500px;
        }
        .hero__checklist li {
          display: flex; align-items: flex-start; gap: .5rem;
          font-size: .875rem; color: var(--muted-fg); line-height: 1.45;
        }
        .hero__checklist li::before {
          content: '';
          display: block; flex-shrink: 0;
          width: 17px; height: 17px; border-radius: 50%;
          background: var(--bg-secondary) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234a7c59' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E") center/10px no-repeat;
          border: 1px solid var(--sage-light);
          margin-top: 2px;
        }

        .doctor-card {
          display: flex; align-items: center; gap: 1.25rem;
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: var(--radius-xl); padding: 1.125rem 1.25rem;
          max-width: 500px; margin-bottom: 1.75rem;
        }
        .doctor-card__img {
          width: 68px; height: 68px; border-radius: var(--radius-md);
          object-fit: cover; flex-shrink: 0;
        }
        .doctor-card__name {
          font-family: var(--font-heading); font-weight: 700; font-size: 1.0625rem;
          color: var(--ink); margin-bottom: .2rem;
        }
        .doctor-card__role { font-size: .8125rem; font-weight: 500; color: var(--accent); margin-bottom: .5rem; }
        .tag-row { display: flex; flex-wrap: wrap; gap: .375rem; }
        .tag {
          font-size: .6875rem; background: var(--bg-secondary);
          color: var(--forest); border: 1px solid var(--border);
          border-radius: 999px; padding: .125rem .625rem;
        }

        .booking-card {
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: var(--radius-xl); box-shadow: var(--shadow-lg);
          overflow: hidden; align-self: start;
          position: sticky; top: calc(var(--nav-h) + 1rem);
        }
        .booking-card__bar { height: 4px; background: linear-gradient(90deg, var(--forest-mid), var(--accent)); }
        .booking-card__body { padding: 1.625rem; }
        .booking-card__eyebrow {
          font-size: .75rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: .08em; color: var(--accent); margin-bottom: .3rem;
        }
        .booking-card__title {
          font-family: var(--font-heading); font-weight: 700; font-size: 1.25rem;
          color: var(--ink); line-height: 1.25; margin-bottom: .375rem;
        }
        .booking-card__sub { font-size: .875rem; color: var(--muted-fg); margin-bottom: 1.375rem; }

        .form { display: flex; flex-direction: column; gap: .875rem; }
        .form__group { display: flex; flex-direction: column; gap: .375rem; }
        .form__label {
          font-size: .75rem; font-weight: 600; text-transform: uppercase;
          letter-spacing: .04em; color: var(--ink-soft);
        }
        .form__row {
          display: flex; align-items: center; gap: .5rem;
          background: var(--bg-input); border: 1.5px solid var(--border);
          border-radius: var(--radius-sm); padding: .6875rem .875rem;
          transition: border-color .2s;
        }
        .form__row:focus-within { border-color: var(--forest-mid); }
        .form__row iconify-icon { color: var(--muted-fg); font-size: .875rem; }
        .form__row input, .form__row select {
          flex: 1; border: none; background: transparent;
          font-size: .9375rem; color: var(--ink); outline: none;
        }
        .form__row select { appearance: none; }
        .form__row--select > iconify-icon:last-child { margin-left: auto; }

        .booking-card__footer {
          display: flex; align-items: center; justify-content: space-between;
          margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--border);
        }
        .p-strike { font-size: .875rem; text-decoration: line-through; color: var(--muted-fg); margin-right: .25rem; }
        .p-main  { font-family: var(--font-heading); font-size: 1.375rem; font-weight: 700; color: var(--forest); }
        .p-gst   { font-size: .8125rem; color: var(--muted-fg); }
        .secure { display: flex; align-items: center; gap: .25rem; font-size: .8125rem; color: var(--muted-fg); }

        .stats-bar { background: var(--forest); padding-block: 2.25rem; }
        .stats-grid {
          display: grid; grid-template-columns: repeat(4,1fr);
        }
        .stat-item {
          display: flex; align-items: center; gap: 1rem;
          padding-inline: 2rem;
          border-right: 1px solid rgba(255,255,255,.15);
        }
        .stat-item:first-child { padding-left: 0; }
        .stat-item:last-child  { border-right: none; padding-right: 0; }
        .stat-item__icon {
          width: 42px; height: 42px;
          background: rgba(255,255,255,.1); border-radius: var(--radius-sm);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .stat-item__icon iconify-icon { color: rgba(255,255,255,.85); font-size: 1.125rem; }
        .stat-item__num {
          font-family: var(--font-heading); font-size: 1.625rem; font-weight: 700;
          color: var(--white); line-height: 1; margin-bottom: .2rem;
        }
        .stat-item__label { font-size: .75rem; color: rgba(255,255,255,.65); }

        .problem-list { margin-bottom: 2rem; }
        .problem-list li {
          display: flex; align-items: flex-start; gap: .875rem;
          padding-block: .75rem; border-bottom: 1px solid var(--border);
          font-size: .9375rem; color: var(--muted-fg); line-height: 1.55;
        }
        .problem-list li:last-child { border-bottom: none; }
        .quote-block {
          background: var(--bg-secondary); border-left: 4px solid var(--forest);
          border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
          padding: 1.125rem 1.375rem;
          font-size: .9375rem; color: var(--forest); font-weight: 500; line-height: 1.6;
        }
        .achieve-card {
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: var(--radius-xl); overflow: hidden;
        }
        .achieve-card__head {
          padding: 1.25rem 1.5rem 1rem; border-bottom: 1px solid var(--border);
        }
        .achieve-card__head p { font-size: .9rem; color: var(--muted-fg); margin-top: .5rem; }
        .achieve-list li {
          display: flex; align-items: flex-start; gap: 1rem;
          padding: 1rem 1.5rem; border-bottom: 1px solid var(--border);
        }
        .achieve-list li:last-child { border-bottom: none; }
        .achieve-list strong { display: block; font-size: .9375rem; color: var(--ink-soft); margin-bottom: .125rem; }
        .achieve-list span   { font-size: .8125rem; color: var(--muted-fg); }
        .achieve-card__foot  { padding: 1rem 1.5rem; background: var(--bg-cream); }

        .drverma-card {
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: var(--radius-xl); overflow: hidden;
        }
        .drverma-card__img {
          width: 100%; height: 340px; object-fit: cover; object-position: bottom;
        }
        .drverma-card__body { padding: 2rem; }
        .drverma-card__name {
          font-family: var(--font-heading); font-weight: 700; font-size: 1.5rem;
          color: var(--ink); margin-bottom: .25rem;
        }
        .drverma-card__role { font-size: .875rem; color: var(--accent); font-weight: 600; margin-bottom: 1.25rem; }
        .verma-pillars {
          display: flex; flex-direction: column; gap: .625rem; margin-top: 1.5rem;
        }
        .verma-pillar {
          display: flex; align-items: center; gap: .875rem;
          background: var(--bg-secondary); border: 1px solid var(--border);
          border-radius: var(--radius-md); padding: .875rem 1rem;
        }
        .verma-pillar__icon {
          width: 36px; height: 36px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .verma-pillar strong { display: block; font-size: .9375rem; color: var(--ink-soft); }
        .verma-pillar span   { font-size: .8125rem; color: var(--muted-fg); }

        .science-layers {
          display: grid; grid-template-columns: repeat(3,1fr);
          gap: 1.25rem; margin-bottom: 2rem;
        }
        .sci-layer {
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: var(--radius-xl); padding: 1.75rem 1.5rem;
          text-align: center;
        }
        .sci-layer__icon {
          width: 56px; height: 56px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1rem;
        }
        .sci-layer__num {
          font-size: .6875rem; font-weight: 700; letter-spacing: .1em;
          text-transform: uppercase; margin-bottom: .5rem;
        }
        .sci-layer h3 {
          font-family: var(--font-heading); font-weight: 700; font-size: 1.125rem;
          color: var(--ink); margin-bottom: .25rem;
        }
        .sci-layer__sub { font-size: .8125rem; color: var(--accent); font-weight: 600; margin-bottom: .75rem; }
        .sci-layer p { font-size: .875rem; color: var(--muted-fg); line-height: 1.65; }
        .science-note {
          background: var(--bg-warm); border: 1px solid var(--border);
          border-left: 4px solid var(--forest); border-radius: 0 var(--radius-md) var(--radius-md) 0;
          padding: 1.125rem 1.5rem;
          font-size: .9375rem; color: var(--forest); font-weight: 500; line-height: 1.6;
          margin-bottom: 2rem;
        }

        .pricing-wrap {
          max-width: 800px; margin-inline: auto;
          border-radius: var(--radius-xl); overflow: hidden;
          border: 1px solid rgba(196,168,130,.3);
          background: rgba(255,255,255,.05); backdrop-filter: blur(8px);
        }
        .pricing-bar { height: 4px; background: linear-gradient(90deg, var(--gold), var(--sage)); }
        .pricing-grid { display: grid; grid-template-columns: 1fr 1fr; }
        .pricing-left {
          padding: 2.25rem 2rem;
          border-right: 1px solid rgba(196,168,130,.2);
        }
        .pricing-right {
          padding: 2.25rem 2rem;
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; text-align: center;
        }
        .pricing-eyebrow {
          font-size: .75rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: .1em; color: var(--gold); margin-bottom: 1.5rem;
        }
        .include-list { display: flex; flex-direction: column; gap: 1.125rem; }
        .include-list li { display: flex; align-items: flex-start; gap: .875rem; }
        .inc-icon {
          width: 34px; height: 34px; border-radius: var(--radius-sm);
          background: rgba(196,168,130,.15); border: 1px solid rgba(196,168,130,.25);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .inc-icon iconify-icon { color: var(--gold); font-size: .9375rem; }
        .include-list strong { display: block; font-size: .9375rem; font-weight: 600; color: var(--white); }
        .include-list span   { font-size: .8125rem; color: rgba(255,255,255,.55); }

        .limited-badge {
          display: inline-flex; align-items: center; gap: .375rem;
          background: rgba(181,137,74,.2); color: var(--gold);
          border: 1px solid rgba(181,137,74,.35);
          border-radius: 999px; padding: .3rem .875rem;
          font-size: .75rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: .04em; margin-bottom: 1.5rem;
        }
        .p-old { font-size: .9375rem; color: rgba(255,255,255,.35); text-decoration: line-through; margin-bottom: .25rem; }
        .p-big { font-family: var(--font-heading); font-size: 3.75rem; font-weight: 700; color: var(--gold); line-height: 1; }
        .p-tax { font-size: .8125rem; color: rgba(255,255,255,.5); margin-top: .25rem; margin-bottom: .875rem; }
        .savings-badge {
          display: inline-block; background: rgba(74,124,89,.3); color: var(--sage-light);
          border: 1px solid rgba(74,124,89,.4); border-radius: 999px;
          padding: .3rem .875rem; font-size: .8125rem; font-weight: 600;
          margin-bottom: 2rem;
        }

        .pricing-trust {
          display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 1.75rem;
          padding: .875rem 2rem;
          border-top: 1px solid rgba(196,168,130,.2);
          background: rgba(0,0,0,.1);
          font-size: .8125rem; color: rgba(255,255,255,.55);
        }
        .pricing-trust span { display: flex; align-items: center; gap: .375rem; }
        .pricing-trust iconify-icon { color: var(--sage); font-size: .75rem; }

        .steps-grid {
          display: grid; grid-template-columns: repeat(4,1fr);
          gap: 1.5rem; position: relative;
        }
        .steps-grid::before {
          content: '';
          position: absolute; top: 40px; left: 12.5%; right: 12.5%;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--border), var(--gold), var(--sage), transparent);
          z-index: 0;
        }
        .step {
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: var(--radius-xl); padding: 1.625rem;
          display: flex; flex-direction: column; align-items: center; text-align: center;
          position: relative; z-index: 1;
        }
        .step__circle {
          width: 80px; height: 80px; border-radius: 50%;
          border: 4px solid var(--bg-cream);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; margin-bottom: 1.25rem;
        }
        .step__inner {
          width: 48px; height: 48px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
        }
        .step__num { font-size: .75rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; opacity: .7; margin-bottom: .5rem; }
        .step h3 { font-family: var(--font-heading); font-weight: 700; font-size: 1.0625rem; color: var(--ink); margin-bottom: .625rem; }
        .step p  { font-size: .875rem; color: var(--muted-fg); line-height: 1.6; }

        .testi-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1.5rem; }
        .testi {
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: var(--radius-xl); padding: 1.625rem;
          display: flex; flex-direction: column; gap: 1rem;
          position: relative; overflow: hidden;
        }
        .testi__big-quote {
          position: absolute; top: .5rem; right: 1.25rem;
          font-family: var(--font-heading); font-size: 5rem; line-height: 1;
          color: var(--border); pointer-events: none; user-select: none;
        }
        .stars { color: var(--accent); font-size: .9375rem; letter-spacing: .1em; }
        .result-pill {
          display: inline-flex; align-items: center; gap: .375rem;
          background: var(--bg-secondary); border: 1px solid var(--sage-light);
          border-radius: 999px; padding: .25rem .75rem;
          font-size: .8125rem; font-weight: 700; color: var(--forest); align-self: flex-start;
        }
        .testi p { font-size: .9375rem; color: var(--muted-fg); line-height: 1.7; font-style: italic; flex: 1; }
        .testi__author {
          display: flex; align-items: center; gap: .875rem;
          padding-top: .875rem; border-top: 1px solid var(--border);
        }
        .testi__author img { width: 42px; height: 42px; border-radius: 50%; object-fit: cover; }
        .testi__name   { font-size: .9375rem; font-weight: 600; color: var(--ink); }
        .testi__detail { font-size: .8125rem; color: var(--muted-fg); }
        .social-proof {
          display: flex; align-items: center; justify-content: center; gap: .875rem;
          margin-top: 2.5rem; font-size: .9375rem; color: var(--muted-fg);
        }
        .avatar-stack { display: flex; }
        .avatar-stack img {
          width: 34px; height: 34px; border-radius: 50%; object-fit: cover;
          border: 2px solid var(--bg); margin-left: -9px;
        }
        .avatar-stack img:first-child { margin-left: 0; }

        .who-list { display: flex; flex-direction: column; gap: .5rem; margin-bottom: 2rem; }
        .who-list li {
          display: flex; align-items: center; gap: .875rem;
          padding: .75rem 1.125rem;
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: var(--radius-md);
          font-size: .9375rem; color: var(--muted-fg);
        }
        .who-card {
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: var(--radius-xl); overflow: hidden;
        }
        .who-card__img { width: 100%; height: 220px; object-fit: cover; }
        .who-card__body { padding: 2rem; }
        .who-card__body h3 { font-family: var(--font-heading); font-weight: 700; font-size: 1.5rem; color: var(--ink); margin-bottom: .875rem; }
        .who-card__body p  { font-size: .9375rem; color: var(--muted-fg); line-height: 1.7; margin-bottom: 1.5rem; }
        .meta-rows { display: flex; flex-direction: column; gap: .5rem; margin-bottom: 1.5rem; }
        .meta-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: .625rem 1rem;
          background: var(--bg-cream); border-radius: var(--radius-md);
          font-size: .9375rem; color: var(--muted-fg);
        }
        .p-forest { color: var(--forest); font-weight: 700; }

        .faq-list {
          max-width: 760px; margin-inline: auto;
          display: flex; flex-direction: column; gap: .75rem;
          margin-bottom: 2.5rem;
        }
        .faq-item {
          background: var(--bg-warm); border: 1.5px solid var(--border);
          border-radius: var(--radius-lg); overflow: hidden;
          transition: border-color .2s;
        }
        .faq-item--open { border-color: var(--forest); }
        .faq-item__q {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
          width: 100%; padding: 1.25rem 1.375rem;
          font-size: .9375rem; font-weight: 600; color: var(--ink); text-align: left;
          background: transparent; cursor: pointer;
        }
        .faq-item--open .faq-item__q { background: var(--bg-secondary); color: var(--forest); }
        .faq-icon {
          width: 26px; height: 26px;
          background: var(--bg-muted); border: 1px solid var(--border);
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; font-size: .8125rem; color: var(--muted-fg);
        }
        .faq-item--open .faq-icon { background: var(--forest); border-color: var(--forest); color: var(--white); }
        .faq-item__a {
          max-height: 0; overflow: hidden;
          transition: max-height .4s ease, padding .4s ease;
          padding: 0 1.375rem;
        }
        .faq-item--open .faq-item__a { max-height: 400px; padding: 0 1.375rem 1.375rem; }
        .faq-item__a p { font-size: .9375rem; color: var(--muted-fg); line-height: 1.75; }

        .final-cta {
          max-width: 640px; margin-inline: auto; text-align: center;
        }
        .trust-row {
          display: flex; align-items: center; justify-content: center; gap: .5rem;
          flex-wrap: wrap; font-size: .875rem; color: var(--muted-fg);
          margin-top: .875rem; margin-bottom: 2rem;
        }
        .trust-row iconify-icon { color: var(--forest); }
        .trust-badges { display: flex; justify-content: center; gap: .75rem; flex-wrap: wrap; }
        .trust-badge {
          display: flex; align-items: center; gap: .5rem;
          background: var(--bg-warm); border: 1px solid var(--border);
          border-radius: 999px; padding: .4rem 1rem;
          font-size: .8125rem; font-weight: 500; color: var(--muted-fg);
        }
        .trust-badge iconify-icon { color: var(--forest); font-size: .875rem; }

        .footer {
          background: #f3f4f6;
          border-top: 1px solid var(--border);
          padding-block: 2rem;
        }
        .footer__inner {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 1.5rem;
        }
        .footer__logo  { height: 42px; width: auto; }
        .footer__contact {
          display: flex; gap: 2rem; flex-wrap: wrap;
        }
        .footer__contact a {
          display: flex; align-items: center; gap: .5rem;
          font-size: .9375rem; font-weight: 500; color: var(--forest);
          transition: opacity .2s;
        }
        .footer__copy { font-size: .8125rem; color: var(--muted-fg); }

        .sticky-bar {
          position: fixed; bottom: 0; left: 0; right: 0; z-index: 99;
          background: var(--bg-warm); border-top: 1px solid var(--border);
          padding-block: .875rem;
          box-shadow: 0 -4px 24px rgba(26,18,8,.08);
          transform: translateY(100%); transition: transform .4s ease;
        }
        .sticky-bar.visible { transform: translateY(0); }
        .sticky-bar__inner {
          display: flex; align-items: center; justify-content: space-between; gap: 1.5rem;
        }
        .sticky-bar__left { display: flex; align-items: center; gap: 1rem; }
        .sticky-bar__ico {
          width: 34px; height: 34px;
          background: var(--bg-secondary); border: 1px solid var(--sage-light);
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
        }
        .sticky-bar__title { font-size: .9375rem; font-weight: 600; color: var(--ink); }
        .sticky-bar__price { text-align: right; }
        .sticky-bar__row   { display: flex; align-items: baseline; gap: .5rem; }

        @media (max-width: 1100px) {
          .hero__inner { grid-template-columns: 1fr 380px; gap: 2.5rem; }
        }
        @media (max-width: 900px) {
          :root { --nav-h: 64px; }
          .hero__inner { grid-template-columns: 1fr; padding-top: 2.5rem; padding-bottom: 2.5rem; }
          .booking-card { position: static; max-width: 540px; }
          .two-col { grid-template-columns: 1fr; }
          .testi-grid { grid-template-columns: 1fr 1fr; }
          .steps-grid { grid-template-columns: 1fr 1fr; }
          .steps-grid::before { display: none; }
          .stats-grid { grid-template-columns: 1fr 1fr; gap: 0; }
          .stat-item {
            border-right: none; padding: 1rem 1.5rem;
            border-bottom: 1px solid rgba(255,255,255,.15);
          }
          .stat-item:nth-child(odd)  { border-right: 1px solid rgba(255,255,255,.15); }
          .stat-item:last-child,
          .stat-item:nth-last-child(2):nth-child(odd) { border-bottom: none; }
          .pricing-grid { grid-template-columns: 1fr; }
          .pricing-left { border-right: none; border-bottom: 1px solid rgba(196,168,130,.2); }
          .science-layers { grid-template-columns: 1fr; }
          .phone-num { display: none; }
          .sticky-bar__left { flex: 1; }
        }
        @media (max-width: 640px) {
          .hero__checklist { grid-template-columns: 1fr; }
          .testi-grid  { grid-template-columns: 1fr; }
          .steps-grid  { grid-template-columns: 1fr; }
          .footer__inner { flex-direction: column; align-items: flex-start; }
          .footer__contact { flex-direction: column; gap: .75rem; }
          .sticky-bar__price { display: none; }
        }
      `}</style>

      {/* Navbar */}
      <header className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="container navbar__inner">
          <Link to="/" className="brand">
  <img 
    src="https://www.nutrillion.one/static/media/newlogo.bdc9e52c79ec594912a6.png" 
    alt="Nutrillion" 
    className="brand__logo" 
  />
</Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span className="phone-num">
              <iconify-icon icon="lucide:phone"></iconify-icon>&nbsp;9557439399
            </span>
            <button className="btn btn--primary" onClick={() => scrollToSection('#book')}>Book Consultation</button>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="hero" id="home" ref={heroRef}>
        <div className="hero__blob hero__blob--tr"></div>
        <div className="hero__blob hero__blob--bl"></div>
        <div className="container hero__inner">
          <div>
            <div className="badge"><span className="badge__dot"></span>India's #1 Nutrigenomics Expert</div>
            <h1 className="h1">Your Health Issues Have a <em>Genetic Root Cause.</em></h1>
            <p className="lead">Struggling with obesity, gut issues, low energy, or hormonal imbalances? Our DNA-based Health Blueprint helps uncover the hidden factors affecting your health and provides a personalized path forward.</p>
            <div className="pill-row">
              <div className="pill"><iconify-icon icon="lucide:users"></iconify-icon> 4,000+ clients</div>
              <div className="pill"><iconify-icon icon="lucide:globe"></iconify-icon> 10+ countries</div>
              <div className="pill"><iconify-icon icon="lucide:award"></iconify-icon> 8+ years</div>
              <div className="pill"><iconify-icon icon="lucide:dna"></iconify-icon> DNA-based</div>
            </div>
            <div className="doctor-card">
              <img   src={drVermaImage} 
 alt="Dr. Rishabh Verma" className="doctor-card__img" />
              <div>
                <div className="doctor-card__name">Dr. Rishabh Verma</div>
                <div className="doctor-card__role">Nutrigenomics Expert &amp; Founder, Nutrillion</div>
                <div className="tag-row">
                  <span className="tag">DNA Nutrition</span>
                  <span className="tag">Gut Microbiome</span>
                  <span className="tag">Metabolic Science</span>
                </div>
              </div>
            </div>
            <ul className="hero__checklist">
              <li>4,000+ clients across 10+ countries</li>
              <li>DNA &amp; genetic profiling specialist</li>
              <li>Gut microbiome diagnostics expert</li>
              <li>Published health researcher</li>
              <li>HNI &amp; corporate health advisor</li>
              <li>8+ years clinical nutrition</li>
            </ul>
          </div>
          
          {/* Booking Form Component with API Integration */}
          <BookConsultForm onBookingSuccess={handleBookingSuccess} scrollToSection={scrollToSection} />
        </div>
      </section>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="container stats-grid">
          <div className="stat-item">
            <div className="stat-item__icon"><iconify-icon icon="lucide:users"></iconify-icon></div>
            <div><div className="stat-item__num">4,000+</div><div className="stat-item__label">Clients Transformed</div></div>
          </div>
          <div className="stat-item">
            <div className="stat-item__icon"><iconify-icon icon="lucide:globe"></iconify-icon></div>
            <div><div className="stat-item__num">10+</div><div className="stat-item__label">Countries Worldwide</div></div>
          </div>
          <div className="stat-item">
            <div className="stat-item__icon"><iconify-icon icon="lucide:award"></iconify-icon></div>
            <div><div className="stat-item__num">8+</div><div className="stat-item__label">Years Experience</div></div>
          </div>
          <div className="stat-item">
            <div className="stat-item__icon"><iconify-icon icon="lucide:dna"></iconify-icon></div>
            <div><div className="stat-item__num">120+</div><div className="stat-item__label">Genetic Markers Analyzed</div></div>
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <section className="section" id="problem">
        <div className="container two-col">
          <div>
            <div className="eyebrow"><span className="eyebrow__line"></span>The Real Problem</div>
            <h2 className="h2">Why Diets &amp; Exercise<br/><em>Still Don't Work for You</em></h2>
            <p className="lead">Most health plans aren't built for your body. Your body works on its own genetic blueprint — until you understand that, real change is nearly impossible.</p>
            <ul className="problem-list">
              <li><span className="xmark"><iconify-icon icon="lucide:x"></iconify-icon></span>You follow strict diets but weight keeps bouncing back</li>
              <li><span className="xmark"><iconify-icon icon="lucide:x"></iconify-icon></span>Exercise routinely but energy stays low all day</li>
              <li><span className="xmark"><iconify-icon icon="lucide:x"></iconify-icon></span>Supplements don't seem to work for your body</li>
              <li><span className="xmark"><iconify-icon icon="lucide:x"></iconify-icon></span>Hormonal issues with no clear root-cause answers</li>
              <li><span className="xmark"><iconify-icon icon="lucide:x"></iconify-icon></span>Generic health plans designed for the "average" person</li>
            </ul>
            <div className="quote-block">
              <strong>The truth:</strong> These approaches manage symptoms — not root causes. Real transformation starts with understanding your genetic blueprint.
            </div>
          </div>
          <div>
            <div className="achieve-card">
              <div className="achieve-card__head">
                <div className="badge" style={{marginBottom:'.5rem'}}>
                  <iconify-icon icon="lucide:check-circle"></iconify-icon> What You Can Achieve
                </div>
                <p>With a DNA-personalized plan from Nutrillion</p>
              </div>
              <ul className="achieve-list">
                <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span><div><strong>Weight &amp; body composition control</strong><span>Targeted at your genetic fat-storage markers</span></div></li>
                <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span><div><strong>All-day sustained energy levels</strong><span>Mitochondrial optimization at a cellular level</span></div></li>
                <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span><div><strong>Stronger gut &amp; smooth digestion</strong><span>Microbiome-targeted protocol</span></div></li>
                <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span><div><strong>Balanced hormones — thyroid &amp; PCOS</strong><span>Hormonal pathways mapped from your DNA</span></div></li>
                <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span><div><strong>Reduced chronic disease risk</strong><span>Prevention-first genetic risk assessment</span></div></li>
              </ul>
              <div className="achieve-card__foot">
                <button className="btn btn--primary btn--full" onClick={() => scrollToSection('#book')}>Get My Personalized Plan <iconify-icon icon="lucide:arrow-right"></iconify-icon></button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dr. Verma Section */}
      <section className="section section--cream" id="about">
        <div className="container two-col two-col--vcenter">
          <div className="drverma-card">
<img 
  src={drVermaImage} 
  alt="Dr. Rishabh Verma" 
  className="drverma-card__img" 
/>            <div className="drverma-card__body">
              <div className="drverma-card__name">Dr. Rishabh Verma</div>
              <div className="drverma-card__role">Nutrigenomics Expert &amp; Founder, Nutrillion</div>
              <div className="tag-row" style={{marginBottom:'1.25rem'}}>
                <span className="tag">DNA Nutrition</span>
                <span className="tag">Gut Microbiome</span>
                <span className="tag">Metabolic Science</span>
                <span className="tag">8+ Years Experience</span>
              </div>
              <div className="verma-pillars">
                <div className="verma-pillar">
                  <div className="verma-pillar__icon" style={{background:'#4a7c59'}}><iconify-icon icon="lucide:dna"></iconify-icon></div>
                  <div><strong>Genetics</strong><span>Understand how your body is designed</span></div>
                </div>
                <div className="verma-pillar">
                  <div className="verma-pillar__icon" style={{background:'#b5894a'}}><iconify-icon icon="lucide:activity"></iconify-icon></div>
                  <div><strong>Gut Health</strong><span>Assess how your body processes food</span></div>
                </div>
                <div className="verma-pillar">
                  <div className="verma-pillar__icon" style={{background:'#2d5a3d'}}><iconify-icon icon="lucide:zap"></iconify-icon></div>
                  <div><strong>Metabolism</strong><span>Evaluate how your body responds</span></div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="eyebrow"><span className="eyebrow__line"></span>Meet the Expert</div>
            <h2 className="h2">Meet the Nutrigenomics Expert<br/><em>Trusted by 4,000+ Clients</em></h2>
            <p className="body-text" style={{marginBottom:'1.25rem'}}>
              Dr. Rishabh Verma is a Nutrigenomics expert who helps people understand their body at a deeper, scientific level. Instead of offering generic diet plans, he focuses on identifying the <strong>root cause</strong> of health issues through DNA-based nutrition, gut health analysis, and metabolic science.
            </p>
            <p className="body-text" style={{marginBottom:'1.25rem'}}>
              With 8+ years of experience and guidance provided to over 4,000 clients across 10+ countries, his work is built on one clear approach: <strong>understand the body first, then fix the problem.</strong>
            </p>
            <p className="body-text" style={{marginBottom:'1.75rem'}}>
              He combines three critical areas that most health plans ignore — Genetics, Gut Health, and Metabolism — to help individuals move away from repeated trial and error and toward clear, personalized solutions that actually work.
            </p>
            <div style={{display:'flex',flexWrap:'wrap',gap:'.625rem',marginBottom:'1.75rem'}}>
              <div className="pill"><iconify-icon icon="lucide:users"></iconify-icon> 4,000+ clients</div>
              <div className="pill"><iconify-icon icon="lucide:globe"></iconify-icon> 10+ countries</div>
              <div className="pill"><iconify-icon icon="lucide:flask-conical"></iconify-icon> Published researcher</div>
              <div className="pill"><iconify-icon icon="lucide:briefcase"></iconify-icon> Corporate health advisor</div>
            </div>
            <button className="btn btn--primary btn--lg" onClick={() => scrollToSection('#book')}>
              Book a Consultation with Dr. Verma <iconify-icon icon="lucide:arrow-right"></iconify-icon>
            </button>
          </div>
        </div>
      </section>

      {/* Science Section */}
      <section className="section" id="science">
        <div className="container">
          <div className="section-center">
            <div className="eyebrow eyebrow--center"><span className="eyebrow__line"></span>The Science<span className="eyebrow__line"></span></div>
            <h2 className="h2" style={{textAlign:'center'}}>Three Layers Your Body<br/><em>Needs Aligned to Heal</em></h2>
            <p className="lead lead--center">Generic diets fail because they ignore your biology. Real results come from addressing all three layers at once.</p>
          </div>
          <div className="science-layers">
            <div className="sci-layer">
              <div className="sci-layer__icon" style={{background:'#e8f0eb'}}>
                <iconify-icon icon="lucide:dna" style={{color:'#4a7c59'}}></iconify-icon>
              </div>
              <div className="sci-layer__num" style={{color:'#4a7c59'}}>01</div>
              <h3>DNA</h3>
              <p className="sci-layer__sub">How You Are Designed</p>
              <p>Your genes determine fat storage, insulin sensitivity, nutrient absorption, and food sensitivities. 120+ markers analyzed to build your plan.</p>
            </div>
            <div className="sci-layer">
              <div className="sci-layer__icon" style={{background:'#f5ede0'}}>
                <iconify-icon icon="lucide:activity" style={{color:'#b5894a'}}></iconify-icon>
              </div>
              <div className="sci-layer__num" style={{color:'#b5894a'}}>02</div>
              <h3>Gut</h3>
              <p className="sci-layer__sub">How You Process Food</p>
              <p>Trillions of gut bacteria affect digestion, immunity, mood, and weight. An imbalanced microbiome can undermine even the best diet.</p>
            </div>
            <div className="sci-layer">
              <div className="sci-layer__icon" style={{background:'#d6e8db'}}>
                <iconify-icon icon="lucide:zap" style={{color:'#2d5a3d'}}></iconify-icon>
              </div>
              <div className="sci-layer__num" style={{color:'#2d5a3d'}}>03</div>
              <h3>Metabolism</h3>
              <p className="sci-layer__sub">How You Respond</p>
              <p>How your cells burn fuel, handle hormones, and produce energy — all unique to you and shaped by the first two layers.</p>
            </div>
          </div>
          <div className="science-note">
            When these three are not aligned, results do not last. This is why every Nutrillion plan starts with a Nutrigenomics assessment — and why Dr. Verma's approach works where others fail.
          </div>
          <div className="section-cta">
            <button className="btn btn--primary btn--lg" onClick={() => scrollToSection('#book')}>Book My DNA Assessment <iconify-icon icon="lucide:arrow-right"></iconify-icon></button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section section--forest" id="pricing">
        <div className="container">
          <div className="section-center">
            <div className="eyebrow eyebrow--light eyebrow--center"><span className="eyebrow__line"></span>Your Investment<span className="eyebrow__line"></span></div>
            <h2 className="h2 h2--light" style={{textAlign:'center'}}>Your Personalized <em>Health Blueprint</em></h2>
            <p className="lead lead--center lead--light">One consultation that uncovers what years of generic diets could not find.</p>
          </div>
          <div className="pricing-wrap">
            <div className="pricing-bar"></div>
            <div className="pricing-grid">
              <div className="pricing-left">
                <div className="pricing-eyebrow">What's Included</div>
                <ul className="include-list">
                  <li><div className="inc-icon"><iconify-icon icon="lucide:user"></iconify-icon></div><div><strong>30–60 min consultation</strong><span>Expert-Led Health Discovery Session</span></div></li>
                  <li><div className="inc-icon"><iconify-icon icon="lucide:file-text"></iconify-icon></div><div><strong>Personalized health blueprint</strong><span>Tailored to your unique biology</span></div></li>
                  <li><div className="inc-icon"><iconify-icon icon="lucide:search"></iconify-icon></div><div><strong>Root-cause analysis</strong><span>Why your body resists change</span></div></li>
                  <li><div className="inc-icon"><iconify-icon icon="lucide:utensils"></iconify-icon></div><div><strong>Diet direction plan</strong><span>Based on your body type</span></div></li>
                  <li><div className="inc-icon"><iconify-icon icon="lucide:message-circle"></iconify-icon></div><div><strong>Expert support access</strong><span>Post-consultation follow-up</span></div></li>
                </ul>
              </div>
              <div className="pricing-right">
                <div className="limited-badge"><iconify-icon icon="lucide:clock"></iconify-icon> Limited Time Offer</div>
                <div className="p-old">₹3,000</div>
                <div className="p-big">₹999</div>
                <div className="p-tax">+ 18% GST</div>
                <div className="savings-badge">You save ₹2,001 today</div>
                <button className="btn btn--gold btn--full" style={{marginBottom:'.75rem'}} onClick={() => scrollToSection('#book')}>
                  Book Consultation Now <iconify-icon icon="lucide:arrow-right"></iconify-icon>
                </button>
                <div className="secure" style={{color:'rgba(255,255,255,.45)',justifyContent:'center'}}>
                  <iconify-icon icon="lucide:lock"></iconify-icon> 100% Secure &amp; Confidential
                </div>
              </div>
            </div>
            <div className="pricing-trust">
              <span><iconify-icon icon="lucide:check"></iconify-icon> Certified Expert</span>
              <span><iconify-icon icon="lucide:check"></iconify-icon> Science-Backed</span>
              <span><iconify-icon icon="lucide:check"></iconify-icon> 10+ Countries</span>
              <span><iconify-icon icon="lucide:check"></iconify-icon> 4,000+ Clients</span>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section section--cream" id="process">
        <div className="container">
          <div className="section-center">
            <div className="eyebrow eyebrow--center"><span className="eyebrow__line"></span>Our Process<span className="eyebrow__line"></span></div>
            <h2 className="h2" style={{textAlign:'center'}}>4 Steps to Your <em>Genetic Health Blueprint</em></h2>
            <p className="lead lead--center">A structured, science-backed process to diagnose the root cause and build a healthy, sustainable body.</p>
          </div>
          <div className="steps-grid">
            <div className="step">
              <div className="step__circle" style={{background:'#e8f0eb'}}>
                <div className="step__inner" style={{background:'#4a7c59'}}><iconify-icon icon="lucide:calendar"></iconify-icon></div>
              </div>
              <div className="step__num" style={{color:'#4a7c59'}}>Step 01</div>
              <h3>Initial Consultation</h3>
              <p>A 30–60 minute expert consultation to review your symptoms, health history, and key concerns in depth.</p>
            </div>
            <div className="step">
              <div className="step__circle" style={{background:'#f5ede0'}}>
                <div className="step__inner" style={{background:'#b5894a'}}><iconify-icon icon="lucide:flask-conical"></iconify-icon></div>
              </div>
              <div className="step__num" style={{color:'#b5894a'}}>Step 02</div>
              <h3>DNA &amp; Gut Testing</h3>
              <p>Simple home-collection kits (saliva &amp; stool) sent to you. Analyzed in certified genetic labs across India.</p>
            </div>
            <div className="step">
              <div className="step__circle" style={{background:'#d6e8db'}}>
                <div className="step__inner" style={{background:'#2d5a3d'}}><iconify-icon icon="lucide:bar-chart-2"></iconify-icon></div>
              </div>
              <div className="step__num" style={{color:'#2d5a3d'}}>Step 03</div>
              <h3>Detailed Analysis</h3>
              <p>Your DNA markers, gut microbiome, and blood reports are synthesized to map your complete metabolic pathways.</p>
            </div>
            <div className="step">
              <div className="step__circle" style={{background:'#f0e9de'}}>
                <div className="step__inner" style={{background:'#7a6a56'}}><iconify-icon icon="lucide:heart-handshake"></iconify-icon></div>
              </div>
              <div className="step__num" style={{color:'#7a6a56'}}>Step 04</div>
              <h3>1:1 Coaching</h3>
              <p>Receive a personalized nutrition plan, supplement protocol, and continuous expert support with follow-ups.</p>
            </div>
          </div>
          <div className="section-cta">
            <button className="btn btn--primary btn--lg" onClick={() => scrollToSection('#book')}>Start My Journey — Book Consultation <iconify-icon icon="lucide:arrow-right"></iconify-icon></button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section" id="testimonials">
        <div className="container">
          <div className="section-center">
            <div className="eyebrow eyebrow--center"><span className="eyebrow__line"></span>Success Stories<span className="eyebrow__line"></span></div>
            <h2 className="h2" style={{textAlign:'center'}}>Real Clients. <em>Real Transformations.</em></h2>
            <p className="lead lead--center">See how personalized nutrition has changed lives across the globe.</p>
          </div>
          <div className="testi-grid">
            <div className="testi">
              <div className="testi__big-quote">"</div>
              <div className="stars">★★★★★</div>
              <div className="result-pill"><iconify-icon icon="lucide:trending-up"></iconify-icon> -12 kg</div>
              <p>I struggled with PCOS and stubborn weight for 6 years. Dr. Rishabh's DNA test revealed my insulin pathways were genetically slow. With his tailored nutrition, I lost 12 kgs and my periods are regular now!</p>
              <div className="testi__author">
                <img src="https://storage.googleapis.com/banani-avatars/avatar/female/25-35/South%20Asian/1" alt="Anjali R." />
                <div>
                  <div className="testi__name">Anjali R.</div>
                  <div className="testi__detail">Lost 12 kg · PCOS Managed</div>
                </div>
              </div>
            </div>
            <div className="testi">
              <div className="testi__big-quote">"</div>
              <div className="stars">★★★★★</div>
              <div className="result-pill"><iconify-icon icon="lucide:trending-up"></iconify-icon> 3 weeks</div>
              <p>My bloating was so severe I dreaded eating out. The gut microbiome profiling highlighted an overgrowth of bad bacteria. Within 3 weeks of the customised plan, my gut feels completely normal!</p>
              <div className="testi__author">
                <img src="https://storage.googleapis.com/banani-avatars/avatar/male/25-35/South%20Asian/2" alt="Vikram S." />
                <div>
                  <div className="testi__name">Vikram S.</div>
                  <div className="testi__detail">IBS · Chronic Bloating Resolved</div>
                </div>
              </div>
            </div>
            <div className="testi">
              <div className="testi__big-quote">"</div>
              <div className="stars">★★★★★</div>
              <div className="result-pill"><iconify-icon icon="lucide:trending-up"></iconify-icon> 2× energy</div>
              <p>As a CEO, afternoon brain fog was killing my productivity. Metabolic profiling revealed my body wasn't utilizing carbs properly. Switching to fats as fuel changed my energy completely!</p>
              <div className="testi__author">
                <img src="https://storage.googleapis.com/banani-avatars/avatar/male/25-35/South%20Asian/3" alt="Rohan M." />
                <div>
                  <div className="testi__name">Rohan M.</div>
                  <div className="testi__detail">Brain Fog Cleared · Energy Boosted</div>
                </div>
              </div>
            </div>
          </div>
          <div className="social-proof">
            <div className="avatar-stack">
              <img src="https://storage.googleapis.com/banani-avatars/avatar/female/25-35/South%20Asian/4" alt="" />
              <img src="https://storage.googleapis.com/banani-avatars/avatar/male/25-35/South%20Asian/5" alt="" />
              <img src="https://storage.googleapis.com/banani-avatars/avatar/female/25-35/South%20Asian/6" alt="" />
              <img src="https://storage.googleapis.com/banani-avatars/avatar/male/25-35/South%20Asian/7" alt="" />
            </div>
            <span><strong>4,000+ clients</strong> transformed across 10+ countries</span>
          </div>
        </div>
      </section>

      {/* Who Section */}
      <section className="section section--cream" id="who">
        <div className="container two-col">
          <div>
            <div className="eyebrow"><span className="eyebrow__line"></span>Is This For Me?</div>
            <h2 className="h2">Who Should Consider<br/><em>This Consultation</em></h2>
            <p className="lead">If even one of these sounds familiar, your body is sending a signal — and it deserves a personalized answer.</p>
            <ul className="who-list">
              <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span>Tried Keto, Detox or IF — but still not getting results</li>
              <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span>Difficulty losing weight despite consistent effort</li>
              <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span>Persistent low or unstable energy throughout the day</li>
              <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span>Frequent bloating, acidity or digestive discomfort</li>
              <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span>Hormonal imbalance — thyroid, PCOS or mood swings</li>
              <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span>Repeated failure with different diet plans</li>
              <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span>Diagnosed with pre-diabetes or insulin resistance</li>
              <li><span className="chk"><iconify-icon icon="lucide:check"></iconify-icon></span>Family history of lifestyle-related diseases</li>
            </ul>
          </div>
          <div>
            <div className="who-card">
              <img src="https://storage.googleapis.com/banani-generated-images/generated-images/4838db38-b8fb-443b-8392-74ea834048e6.jpg" alt="Natural wellness" className="who-card__img" />
              <div className="who-card__body">
                <h3>Yes, This Sounds Like Me</h3>
                <p>One consultation is all it takes to uncover what years of trial-and-error couldn't find. Dr. Rishabh Verma will analyze your unique biology and give you a clear, science-backed path forward.</p>
                <div className="meta-rows">
                  <div className="meta-row">
                    <span>Consultation fee</span>
                    <div><span className="p-strike">₹3,000</span> <strong className="p-forest">₹999 + GST</strong></div>
                  </div>
                </div>
                <button className="btn btn--primary btn--full" onClick={() => scrollToSection('#book')}>Book My Consultation <iconify-icon icon="lucide:arrow-right"></iconify-icon></button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section" id="faq">
        <div className="container">
          <div className="section-center">
            <div className="eyebrow eyebrow--center"><span className="eyebrow__line"></span>Common Questions<span className="eyebrow__line"></span></div>
            <h2 className="h2" style={{textAlign:'center'}}>Frequently Asked <em>Questions</em></h2>
            <p className="lead lead--center">Everything you need to know about DNA-based nutrition and the Nutrillion program.</p>
          </div>
          <div className="faq-list">
            {faqItems.map((item, index) => (
              <div key={index} className={`faq-item ${openFaqIndex === index ? 'faq-item--open' : ''}`} data-faq>
                <button className="faq-item__q" onClick={() => toggleFaq(index)}>
                  <span>{item.question}</span>
                  <span className="faq-icon"><iconify-icon icon={openFaqIndex === index ? "lucide:minus" : "lucide:plus"}></iconify-icon></span>
                </button>
                <div className="faq-item__a">
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <button className="btn btn--primary btn--lg" onClick={() => scrollToSection('#book')}>Still Have Questions? Book a Consultation <iconify-icon icon="lucide:arrow-right"></iconify-icon></button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section section--forest" id="cta">
        <div className="container">
          <div className="final-cta">
            <div className="eyebrow eyebrow--light eyebrow--center"><span className="eyebrow__line"></span>Get Started Today<span className="eyebrow__line"></span></div>
            <h2 className="h2 h2--light" style={{textAlign:'center',marginBottom:'1rem'}}>Take the First Step Toward a<br/><em>Healthier, Disease-Free Life</em></h2>
            <p className="lead lead--center lead--light">Gain clarity on your health with an expert-led consultation that identifies root causes and creates a personalized path forward.</p>
            <button className="btn btn--gold btn--lg btn--full" style={{maxWidth:'420px',margin:'0 auto 1.25rem',display:'flex'}} onClick={() => scrollToSection('#book')}>
              Book My Consultation Now <iconify-icon icon="lucide:arrow-right"></iconify-icon>
            </button>
            <div className="trust-row">
              <span><iconify-icon icon="lucide:shield-check" style={{color:'var(--sage-light)'}}></iconify-icon> 100% Secure &amp; Confidential</span>
              <span style={{color:'rgba(255,255,255,.3)'}}>·</span>
              <span><iconify-icon icon="lucide:clock" style={{color:'var(--sage-light)'}}></iconify-icon> Response within 2 hours</span>
            </div>
            <div className="trust-badges" style={{justifyContent:'center'}}>
              <div className="trust-badge" style={{background:'rgba(255,255,255,.08)',borderColor:'rgba(255,255,255,.15)',color:'rgba(255,255,255,.7)'}}><iconify-icon icon="lucide:award" style={{color:'var(--sage-light)'}}></iconify-icon> Certified Expert</div>
              <div className="trust-badge" style={{background:'rgba(255,255,255,.08)',borderColor:'rgba(255,255,255,.15)',color:'rgba(255,255,255,.7)'}}><iconify-icon icon="lucide:flask-conical" style={{color:'var(--sage-light)'}}></iconify-icon> Science-Backed</div>
              <div className="trust-badge" style={{background:'rgba(255,255,255,.08)',borderColor:'rgba(255,255,255,.15)',color:'rgba(255,255,255,.7)'}}><iconify-icon icon="lucide:globe" style={{color:'var(--sage-light)'}}></iconify-icon> 10+ Countries</div>
              <div className="trust-badge" style={{background:'rgba(255,255,255,.08)',borderColor:'rgba(255,255,255,.15)',color:'rgba(255,255,255,.7)'}}><iconify-icon icon="lucide:users" style={{color:'var(--sage-light)'}}></iconify-icon> 4,000+ Clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer__inner">
          <div className="footer__brand">
            <img src="https://www.nutrillion.one/static/media/newlogo.bdc9e52c79ec594912a6.png" alt="Nutrillion" className="footer__logo" />
          </div>
          <div className="footer__contact">
            <a href="tel:+919557439399"><iconify-icon icon="lucide:phone"></iconify-icon> +91 95574 39399</a>
            <a href="mailto:info@nutrillion.one"><iconify-icon icon="lucide:mail"></iconify-icon> info@nutrillion.one</a>
          </div>
          <p className="footer__copy">© 2026 Nutrillion. All rights reserved.</p>
        </div>
      </footer>

      {/* Sticky Bar */}
      <div className={`sticky-bar ${stickyVisible ? 'visible' : ''}`} id="stickyBar">
        <div className="container sticky-bar__inner">
          <div className="sticky-bar__left">
            <div className="sticky-bar__ico"><iconify-icon icon="lucide:dna"></iconify-icon></div>
            <div>
              <div className="sticky-bar__title">Nutrillion Consultation</div>
            </div>
          </div>
          <div className="sticky-bar__price">
            <div className="sticky-bar__row">
              <span className="p-strike">₹3,000</span>
              <span className="p-main">₹999</span>
              <span className="p-gst">+ GST</span>
            </div>
          </div>
          <button className="btn btn--primary" onClick={() => scrollToSection('#book')}>Book Now <iconify-icon icon="lucide:arrow-right"></iconify-icon></button>
        </div>
      </div>

      {/* Iconify Script */}
      <script src="https://code.iconify.design/iconify-icon/3.0.0/iconify-icon.min.js"></script>
    </div>
  );
};

export default BookConsult;